import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Trash2, Mail, FileText, Calendar, DollarSign, User, Phone, MapPin, Clock, CheckCircle, XCircle, Send, Save, X, Users, Eye, Paperclip, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import EmailDetailModal from '@/components/EmailDetailModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InquiryDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [inquiry, setInquiry] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingContract, setSendingContract] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Edit mode states
  const [editingSection, setEditingSection] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState(null);

  useEffect(() => {
    fetchInquiryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInquiryData = async () => {
    try {
      setLoading(true);
      const [inquiryRes, contractsRes, emailsRes] = await Promise.all([
        axios.get(`${API}/inquiries/${id}`),
        axios.get(`${API}/contracts`).catch(() => ({ data: [] })),
        axios.get(`${API}/email-logs`).catch(() => ({ data: [] }))
      ]);
      
      setInquiry(inquiryRes.data);
      
      // Filter contracts for this inquiry
      const inquiryContracts = (contractsRes.data || []).filter(c => c.inquiry_id === id);
      setContracts(inquiryContracts);
      
      // Filter emails for this inquiry
      const inquiryEmail = inquiryRes.data?.email?.toLowerCase();
      const filteredEmails = (emailsRes.data || []).filter(
        log => log.recipient_email?.toLowerCase() === inquiryEmail
      );
      setEmailLogs(filteredEmails);
    } catch (error) {
      toast.error('Failed to load inquiry details');
      navigate('/admin/inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this inquiry?')) return;
    
    try {
      await axios.delete(`${API}/inquiries/${id}`);
      toast.success('Inquiry deleted');
      navigate('/admin/inquiries');
    } catch (error) {
      toast.error('Failed to delete inquiry');
    }
  };

  const handleSendEmail = () => {
    const params = new URLSearchParams({
      to: inquiry.email || '',
      firstName: inquiry.first_name || '',
      lastName: inquiry.last_name || '',
      checkIn: inquiry.check_in_date || '',
      checkOut: inquiry.check_out_date || '',
      guests: inquiry.num_occupants || '',
      rentalAmount: inquiry.rental_amount || '',
      totalWithHst: inquiry.total_with_hst || '',
      type: 'inquiry',
    });
    navigate(`/admin/email?${params.toString()}`);
  };

  const handleSendContract = async () => {
    setSendingContract(true);
    try {
      // Send contract using inquiry data
      await axios.post(`${API}/contracts/inquiry`, { inquiry_id: id });
      toast.success(`Contract sent to ${inquiry.email}`);
      fetchInquiryData();
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (detail.includes('pending')) {
        toast.error('A pending contract already exists.');
      } else if (detail.includes('signed')) {
        toast.error('A signed contract already exists.');
      } else {
        toast.error(detail || 'Failed to send contract');
      }
    } finally {
      setSendingContract(false);
    }
  };

  const handleResendContract = async (contractId) => {
    try {
      await axios.post(`${API}/contracts/${contractId}/resend`);
      toast.success('Contract email resent');
      fetchInquiryData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend contract');
    }
  };

  const handleConvertToRenter = async () => {
    if (!window.confirm('Convert this inquiry to a confirmed renter?')) return;
    
    try {
      const response = await axios.post(`${API}/inquiries/${id}/convert-to-renter`);
      toast.success('Converted to renter');
      navigate(`/admin/renters/${response.data.renter_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert');
    }
  };

  const handlePreviewContract = async () => {
    try {
      setPreviewLoading(true);
      // Get contract preview data for inquiry
      const response = await axios.post(`${API}/contracts/preview-inquiry`, { inquiry_id: id });
      setPreviewData(response.data);
      setShowPreviewModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate contract preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadPreviewPdf = async () => {
    try {
      const response = await axios.post(`${API}/contracts/preview-inquiry-pdf`, { inquiry_id: id }, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      toast.success('Contract PDF opened in new tab');
    } catch (error) {
      toast.error('Failed to generate PDF preview');
    }
  };

  // Start editing a section
  const startEditing = (section) => {
    setEditingSection(section);
    if (section === 'info') {
      setEditData({
        first_name: inquiry.first_name || '',
        last_name: inquiry.last_name || '',
        email: inquiry.email || '',
        phone_cell: inquiry.phone_cell || '',
        phone_home: inquiry.phone_home || '',
        home_address: inquiry.home_address || '',
        city: inquiry.city || '',
        province_state: inquiry.province_state || '',
        postal_code: inquiry.postal_code || '',
        country: inquiry.country || 'Canada',
        num_occupants: inquiry.num_occupants || '',
        age_range: inquiry.age_range || '',
        how_heard: inquiry.how_heard || '',
        questions_comments: inquiry.questions_comments || '',
        notes: inquiry.notes || '',
      });
    } else if (section === 'rental') {
      setEditData({
        check_in_date: inquiry.check_in_date || '',
        check_out_date: inquiry.check_out_date || '',
        status: inquiry.status || 'new',
      });
    } else if (section === 'payment') {
      setEditData({
        rental_amount: inquiry.rental_amount || 0,
        hst_percent: inquiry.hst_percent || 13,
        total_with_hst: inquiry.total_with_hst || 0,
      });
    }
  };

  const cancelEditing = () => {
    setEditingSection(null);
    setEditData({});
  };

  const saveSection = async () => {
    setSaving(true);
    try {
      let updatePayload = { ...editData };
      
      // Auto-calculate total if editing payment section
      if (editingSection === 'payment') {
        const rental = parseFloat(editData.rental_amount) || 0;
        const hstPercent = parseFloat(editData.hst_percent) || 0;
        updatePayload.total_with_hst = Math.round(rental * (1 + hstPercent / 100) * 100) / 100;
        updatePayload.hst_amount = Math.round(rental * hstPercent / 100 * 100) / 100;
      }
      
      await axios.put(`${API}/inquiries/${id}`, updatePayload);
      toast.success('Changes saved');
      setEditingSection(null);
      setEditData({});
      fetchInquiryData();
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const updateEditData = (field, value) => {
    setEditData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Auto-calculate total when rental_amount or hst_percent changes
      if (field === 'rental_amount' || field === 'hst_percent') {
        const rental = parseFloat(field === 'rental_amount' ? value : prev.rental_amount) || 0;
        const hstPercent = parseFloat(field === 'hst_percent' ? value : prev.hst_percent) || 0;
        updated.total_with_hst = Math.round(rental * (1 + hstPercent / 100) * 100) / 100;
      }
      
      return updated;
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'America/Toronto'
      });
    }
    // Handle full ISO datetime strings
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'America/Toronto'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return { label: 'New', color: 'bg-blue-100 text-blue-800' };
      case 'contacted':
        return { label: 'Contacted', color: 'bg-purple-100 text-purple-800' };
      case 'pending':
        return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
      case 'confirmed':
        return { label: 'Confirmed', color: 'bg-green-100 text-green-800' };
      case 'declined':
        return { label: 'Declined', color: 'bg-red-100 text-red-800' };
      default:
        return { label: status || 'New', color: 'bg-gray-100 text-gray-600' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1B4D3E]"></div>
      </div>
    );
  }

  if (!inquiry) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Inquiry not found</p>
        <Button variant="outline" onClick={() => navigate('/admin/inquiries')} className="mt-4">
          Back to Inquiries
        </Button>
      </div>
    );
  }

  const statusBadge = getStatusBadge(inquiry.status);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/inquiries')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#1B4D3E]">
              {inquiry.first_name} {inquiry.last_name}
            </h1>
            <p className="text-gray-500">{inquiry.email}</p>
          </div>
          <Badge className={statusBadge.color}>{statusBadge.label}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="text-red-600 hover:text-red-700" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Preview Contract button - always available */}
            <Button variant="outline" onClick={handlePreviewContract} disabled={previewLoading} className="text-purple-600 hover:text-purple-700">
              <Eye className="w-4 h-4 mr-2" /> {previewLoading ? 'Loading...' : 'Preview Contract'}
            </Button>
            {/* Contract button - context aware */}
            {contracts.length === 0 ? (
              <Button onClick={handleSendContract} disabled={sendingContract} className="bg-blue-600 hover:bg-blue-700">
                <FileText className="w-4 h-4 mr-2" /> 
                {sendingContract ? 'Sending...' : 'Send Contract'}
              </Button>
            ) : contracts.some(c => c.status === 'signed') ? (
              <Button variant="outline" disabled className="text-green-600">
                <CheckCircle className="w-4 h-4 mr-2" /> Contract Signed
              </Button>
            ) : (
              <Button variant="outline" onClick={() => handleResendContract(contracts.find(c => c.status === 'pending')?.id)} className="text-yellow-600 hover:text-yellow-700">
                <Send className="w-4 h-4 mr-2" /> Resend Contract
              </Button>
            )}
            <Button variant="outline" onClick={handleSendEmail}>
              <Mail className="w-4 h-4 mr-2" /> Send Email
            </Button>
            <Button variant="outline" onClick={handleConvertToRenter} className="text-amber-600 hover:text-amber-700">
              <Users className="w-4 h-4 mr-2" /> Convert to Renter
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contact Information */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" /> Contact Information
            </CardTitle>
            {editingSection === 'info' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={cancelEditing} disabled={saving}>
                  <X className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={saveSection} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163d32]">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => startEditing('info')}>
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editingSection === 'info' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input value={editData.first_name} onChange={(e) => updateEditData('first_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input value={editData.last_name} onChange={(e) => updateEditData('last_name', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={editData.email} onChange={(e) => updateEditData('email', e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Cell Phone</Label>
                    <Input value={editData.phone_cell} onChange={(e) => updateEditData('phone_cell', e.target.value)} />
                  </div>
                  <div>
                    <Label>Home Phone</Label>
                    <Input value={editData.phone_home} onChange={(e) => updateEditData('phone_home', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={editData.home_address} onChange={(e) => updateEditData('home_address', e.target.value)} />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input value={editData.city} onChange={(e) => updateEditData('city', e.target.value)} />
                  </div>
                  <div>
                    <Label>Province</Label>
                    <Input value={editData.province_state} onChange={(e) => updateEditData('province_state', e.target.value)} />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input value={editData.postal_code} onChange={(e) => updateEditData('postal_code', e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Number of Guests</Label>
                    <Input type="number" value={editData.num_occupants} onChange={(e) => updateEditData('num_occupants', e.target.value)} />
                  </div>
                  <div>
                    <Label>Age Range</Label>
                    <Input value={editData.age_range} onChange={(e) => updateEditData('age_range', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>How Did You Hear About Us?</Label>
                  <Input value={editData.how_heard} onChange={(e) => updateEditData('how_heard', e.target.value)} />
                </div>
                <div>
                  <Label>Comments/Questions</Label>
                  <Textarea value={editData.questions_comments} onChange={(e) => updateEditData('questions_comments', e.target.value)} rows={3} />
                </div>
                <div>
                  <Label>Internal Notes</Label>
                  <Textarea value={editData.notes} onChange={(e) => updateEditData('notes', e.target.value)} rows={2} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Cell Phone</p>
                  <p className="font-medium">{inquiry.phone_cell || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Home Phone</p>
                  <p className="font-medium">{inquiry.phone_home || '-'}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500">Address</p>
                  <p className="font-medium">
                    {inquiry.home_address && `${inquiry.home_address}, `}
                    {inquiry.city && `${inquiry.city}, `}
                    {inquiry.province_state} {inquiry.postal_code}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Guests</p>
                  <p className="font-medium">{inquiry.num_occupants || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Age Range</p>
                  <p className="font-medium">{inquiry.age_range || '-'}</p>
                </div>
                {inquiry.how_heard && (
                  <div className="col-span-2">
                    <p className="text-gray-500">How They Heard About Us</p>
                    <p className="font-medium">{inquiry.how_heard}</p>
                  </div>
                )}
                {inquiry.questions_comments && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Questions/Comments</p>
                    <p className="font-medium whitespace-pre-wrap">{inquiry.questions_comments}</p>
                  </div>
                )}
                {inquiry.notes && (
                  <div className="col-span-2">
                    <p className="text-gray-500">Internal Notes</p>
                    <p className="font-medium whitespace-pre-wrap">{inquiry.notes}</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rental Period */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" /> Rental Period
            </CardTitle>
            {editingSection === 'rental' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={cancelEditing} disabled={saving}>
                  <X className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={saveSection} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163d32]">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => startEditing('rental')}>
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {editingSection === 'rental' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Check-in Date</Label>
                    <Input type="date" value={editData.check_in_date} onChange={(e) => updateEditData('check_in_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>Check-out Date</Label>
                    <Input type="date" value={editData.check_out_date} onChange={(e) => updateEditData('check_out_date', e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label>Status</Label>
                  <Select value={editData.status} onValueChange={(value) => updateEditData('status', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Check-in</p>
                  <p className="font-medium text-lg">{formatDate(inquiry.check_in_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Check-out</p>
                  <p className="font-medium text-lg">{formatDate(inquiry.check_out_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Nights</p>
                  <p className="font-medium">{inquiry.num_nights || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="font-medium">{formatDate(inquiry.created_at)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" /> Pricing
            </CardTitle>
            {editingSection === 'payment' ? (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" onClick={cancelEditing} disabled={saving}>
                  <X className="h-4 w-4" />
                </Button>
                <Button size="sm" onClick={saveSection} disabled={saving} className="bg-[#1B4D3E] hover:bg-[#163d32]">
                  <Save className="h-4 w-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="ghost" onClick={() => startEditing('payment')}>
                <Edit2 className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {editingSection === 'payment' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Rental Amount ($)</Label>
                    <Input type="number" value={editData.rental_amount} onChange={(e) => updateEditData('rental_amount', e.target.value)} />
                  </div>
                  <div>
                    <Label>HST (%)</Label>
                    <Input type="number" value={editData.hst_percent} onChange={(e) => updateEditData('hst_percent', e.target.value)} />
                  </div>
                  <div>
                    <Label>Total with HST ($)</Label>
                    <Input 
                      type="number" 
                      value={editData.total_with_hst?.toFixed(2) || '0.00'} 
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/calculate-price`, {
                        params: {
                          check_in_date: inquiry.check_in_date,
                          check_out_date: inquiry.check_out_date
                        }
                      });
                      setEditData(prev => ({
                        ...prev,
                        rental_amount: response.data.rental_amount,
                        hst_percent: response.data.hst_percent,
                        total_with_hst: response.data.total_with_hst
                      }));
                      toast.success('Prices calculated');
                    } catch (error) {
                      toast.error('Failed to calculate prices');
                    }
                  }}
                  className="w-full"
                >
                  <DollarSign className="h-4 w-4 mr-2" /> Calculate Prices
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-6">
                <div>
                  <p className="text-gray-500 text-sm">Rental Amount</p>
                  <p className="font-semibold text-xl">{formatCurrency(inquiry.rental_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">HST ({inquiry.hst_percent || 13}%)</p>
                  <p className="font-semibold text-xl">{formatCurrency(inquiry.hst_amount)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-sm">Total</p>
                  <p className="font-semibold text-xl text-[#1B4D3E]">{formatCurrency(inquiry.total_with_hst)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Contracts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Contracts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contracts.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <FileText className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No contracts sent yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={handleSendContract} disabled={sendingContract}>
                  {sendingContract ? 'Sending...' : 'Send Contract'}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {contracts.map((contract) => (
                  <div key={contract.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          contract.status === 'signed' ? 'bg-green-100 text-green-800' :
                          contract.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {contract.status === 'signed' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {contract.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                          {contract.status === 'expired' && <XCircle className="h-3 w-3 mr-1" />}
                          {contract.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Sent: {formatDate(contract.sent_at)}
                        {contract.signed_at && ` • Signed: ${formatDate(contract.signed_at)}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Email History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Email History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emailLogs.length === 0 ? (
              <div className="text-center py-6 text-gray-500">
                <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No emails sent yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={handleSendEmail}>
                  Send Email
                </Button>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {emailLogs.map((log) => (
                  <div 
                    key={log.id} 
                    className="p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => setSelectedEmail(log)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{log.subject}</p>
                        <p className="text-xs text-gray-500">{formatDate(log.sent_at)}</p>
                        {(log.attachments_count > 0 || log.attachments?.length > 0) && (
                          <p className="text-xs text-[#1B4D3E] flex items-center gap-1 mt-1">
                            <Paperclip className="w-3 h-3" />
                            {log.attachments?.length || log.attachments_count} attachment(s)
                          </p>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-[#1B4D3E] hover:text-[#153E32] flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedEmail(log);
                        }}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Email Detail Modal */}
      <EmailDetailModal 
        email={selectedEmail} 
        open={!!selectedEmail} 
        onClose={() => setSelectedEmail(null)} 
      />

      {/* Contract Preview Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" /> Contract Preview
            </DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-6">
              {/* Renter Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Guest Information</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Name:</span> {previewData.renter?.name}</div>
                  <div><span className="text-gray-500">Email:</span> {previewData.renter?.email}</div>
                  <div><span className="text-gray-500">Phone:</span> {previewData.renter?.phone || 'N/A'}</div>
                  <div><span className="text-gray-500">Address:</span> {previewData.renter?.address || 'N/A'}</div>
                  <div><span className="text-gray-500">City:</span> {previewData.renter?.city || 'N/A'}</div>
                  <div><span className="text-gray-500">Province:</span> {previewData.renter?.province || 'N/A'}</div>
                </div>
              </div>

              {/* Property & Rental Details */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Rental Details</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Property:</span> {previewData.property?.name}</div>
                  <div><span className="text-gray-500">Location:</span> {previewData.property?.address}</div>
                  <div><span className="text-gray-500">Check-in:</span> {previewData.rental?.check_in_date} at {previewData.rental?.check_in_time}</div>
                  <div><span className="text-gray-500">Check-out:</span> {previewData.rental?.check_out_date} at {previewData.rental?.check_out_time}</div>
                  <div><span className="text-gray-500">Nights:</span> {previewData.rental?.num_nights}</div>
                </div>
              </div>

              {/* Financial Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Financial Summary</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Rental Amount:</span> ${previewData.financial?.rental_amount?.toLocaleString()}</div>
                  <div><span className="text-gray-500">HST ({previewData.financial?.hst_percent}%):</span> ${previewData.financial?.hst_amount?.toLocaleString()}</div>
                  <div className="font-semibold"><span className="text-gray-500">Total:</span> ${previewData.financial?.total_amount?.toLocaleString()}</div>
                  <div><span className="text-gray-500">Security Deposit:</span> ${previewData.financial?.security_deposit?.toLocaleString()}</div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Payment Methods</h3>
                <div className="text-sm space-y-1">
                  <div>E-Transfer: {previewData.payment_methods?.etransfer_email} (password: {previewData.payment_methods?.etransfer_password})</div>
                  <div>Cheque: Payable to {previewData.payment_methods?.cheque_payable_to}</div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h3 className="font-semibold text-[#1B4D3E] mb-3">Terms & Conditions (shown to guest)</h3>
                <div 
                  className="text-sm max-h-64 overflow-y-auto prose prose-sm"
                  dangerouslySetInnerHTML={{ __html: previewData.terms_and_conditions }}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={handleDownloadPreviewPdf}>
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
                <Button variant="outline" onClick={() => setShowPreviewModal(false)}>
                  Cancel
                </Button>
                <Button onClick={() => { setShowPreviewModal(false); handleSendContract(); }} className="bg-[#1B4D3E] hover:bg-[#153E32]">
                  <Send className="w-4 h-4 mr-2" /> Send Contract
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
